<script>
import dirModel from '@/model/dir';

import BaseInput from '@/components/BaseInput.vue';
import BaseRadio from '@/components/BaseRadio.vue';
import BaseTable from '@/components/base/BaseTable.vue';
import BaseTableCell from '@/components/base/BaseTableCell.vue';
import EllipsisMenu from '@/components/EllipsisMenu.vue';
import EmptyList from '@/components/ui/EmptyList.vue';

// Высота выпадающего меню строки: два пункта плюс отступы контейнера.
// Меряется только для выбора направления, поэтому хватает константы
const MENU_HEIGHT = 100;

/**
 * Попап выбора элемента справочника: таблица с колонками из конфига,
 * поиск и CRUD элементов через универсальную форму.
 *
 * Резолвится объектом { value, items }: value — выбранное значение
 * (config.getValue) или null, если попап закрыли; items — актуальный список,
 * чтобы вызывающему полю не пришлось перезапрашивать его после закрытия.
 */
export default {
  name: 'BaseDirListPopupWrappable',
  components: {
    BaseInput,
    BaseRadio,
    BaseTable,
    EllipsisMenu,
    EmptyList,
  },
  data() {
    return {
      config: null,
      filter: {},
      context: {},
      items: [],
      search: '',
      selected: null,
      menuUp: false,
      // права на правку справочника: canEdit/canRemove решают по элементу,
      // строка без доступных действий остаётся вовсе без меню
      canCreate: true,
      canEdit: () => true,
      canRemove: () => true,
    };
  },
  computed: {
    columns() {
      return this.config?.getColumns(this.context) ?? [];
    },
    // Колонка описывает отрисовку методом render(item); BaseTable ждёт
    // component: { is, props } — приводим одно к другому, оставляя
    // возможность задать компонент ячейки в конфиге напрямую.
    headers() {
      return this.columns.map(column => ({
        ...column,
        component: column.component ?? {
          is: BaseTableCell,
          props: item => ({ value: column.render ? column.render(item) : '' }),
        },
      }));
    },
    itemsFiltered() {
      const search = this.search.toLowerCase().replace(/ /g, '');
      if (!search) return this.items;

      return this.items.filter(
        item => this.config.getSearchIndex(item).toLowerCase().replace(/ /g, '').includes(search),
      );
    },
  },
  methods: {
    init(params = {}) {
      this.config = params.config;
      this.filter = params.filter ?? {};
      this.context = params.context ?? {};
      this.selected = params.value ?? null;
      this.canCreate = params.canCreate ?? true;
      this.canEdit = params.canEdit ?? (() => true);
      this.canRemove = params.canRemove ?? (() => true);

      this.fetchList();
    },
    fetchList() {
      return this.config.getList(this.filter)
        .then(items => { this.items = items; })
        .catch(() => { this.items = []; });
    },
    isSelected(item) {
      return this.config.getValue(item) === this.selected;
    },
    cellStyle(header) {
      return header.width ? `min-width: ${header.width}%; width: ${header.width}%` : '';
    },
    // Список скроллится, поэтому у строк рядом с его нижней границей меню
    // нужно раскрывать вверх — иначе скролл-контейнер его обрежет. Направление
    // выбираем по факту, до открытия: на mousedown, который приходит раньше click
    setMenuDirection(e) {
      const body = e.currentTarget.closest('.body');
      if (!body) return;

      const trigger = e.currentTarget.getBoundingClientRect();
      this.menuUp = trigger.bottom + MENU_HEIGHT > body.getBoundingClientRect().bottom;
    },
    menuItems(item) {
      const items = [];

      if (this.canEdit(item)) {
        items.push({ text: 'Редактировать', icon: 'edit', handler: () => this.edit(item) });
      }
      if (this.canRemove(item)) {
        items.push({ text: 'Удалить', icon: 'trash', handler: () => this.remove(item) });
      }

      return items;
    },
    async add() {
      const created = await dirModel.add(this.config, { filter: this.filter, context: this.context });
      if (!created) return;

      await this.fetchList();
      this.selected = this.config.getValue(created);
    },
    async edit(item) {
      const updated = await dirModel.edit(this.config, item, { filter: this.filter, context: this.context });
      if (updated) this.fetchList();
    },
    async remove(item) {
      const removed = await dirModel.remove(this.config, item);
      if (!removed) return;

      if (this.isSelected(item)) this.selected = null;
      this.fetchList();
    },
    // Вместе с выбором отдаём и сам список: он уже загружен и свежее того,
    // что лежит в поле, — так полю не нужен повторный запрос после закрытия
    select() {
      this.$emit('finish', { value: this.selected, items: this.items });
    },
    cancel() {
      this.$emit('finish', { value: null, items: this.items });
    },
  },
};
</script>

<template lang="pug">
  .b-base-dir-list(v-if="config")
    ._top
      ._title {{ config.title }}
      BaseInput._search(v-model="search" label="Поиск" iconName="search")
    ._table-wrap
      BaseTable._table(:headers="headers" :items="itemsFiltered" v-slot="{ items, headers: headersSplit }")
        EmptyList(v-if="!items.length") Нет данных
        ._row(
          v-for="item in items"
          :key="item.id"
          :class="{ selected: isSelected(item) }"
          @click="selected = config.getValue(item)"
        )
          ._cell(v-for="({ header }, index) in headersSplit" :key="index" :style="cellStyle(header)")
            BaseRadio._radio(v-if="index === 0" :checked="isSelected(item)" @check="selected = config.getValue(item)")
            component(:is="header.component.is" v-bind="header.component.props(item)")
            EllipsisMenu._menu(
              v-if="index === headersSplit.length - 1 && menuItems(item).length"
              :items="menuItems(item)"
              :up="menuUp"
              left
              @click.native.stop
              @mousedown.native="setMenuDirection"
            )
    ._buttons(:class="{ 'no-add': !canCreate }")
      Btn(v-if="canCreate" dark @click="add")
        Icon(name="plus" pointer)
        | Добавить
      Btn(grey @click="cancel") Отмена
      Btn(green @click="select" :disabled="!selected") Выбрать
</template>

<style scoped lang="scss">
// Отступы самого BasePopup: margin 40px сверху и снизу плюс padding 32px
$popup-chrome: 150px;

// Попап занимает фиксированную долю экрана и разложен в колонку: заголовок,
// список и кнопки. Тянется только список — поэтому верхняя и нижняя границы
// попапа всегда на экране, а его высота не скачет, когда фильтр оставляет мало
// записей или прячет пагинацию
.base-dir-list {
  display: flex;
  flex-direction: column;
  width: 900px;
  max-width: 100%;
  height: calc(100vh - #{$popup-chrome});
  min-height: 320px;

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    margin-bottom: 30px;
    flex-shrink: 0;
  }

  &__title {
    font-size: 36px;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0.02em;
  }

  &__search {
    width: 260px;
  }

  // Остаток высоты попапа отдаём таблице, а внутри неё — списку строк.
  // min-height: 0 обязателен: без него flex-элемент не может стать меньше
  // своего содержимого и скролл не появляется
  &__table-wrap {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
  }

  &__table {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  // .body — служебный класс из BaseTable в обход БЭМ. Скроллится только он:
  // шапка таблицы и пагинация остаются на месте. Сам BaseTable не трогаем —
  // такая раскладка нужна только здесь, в попапе справочника
  &__table ::v-deep .body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    // строки не должны упираться в скроллбар вплотную
    padding-right: 4px;
  }

  &__table ::v-deep .header {
    flex-shrink: 0;
  }

  // Ниже этой ширины таблица уже не помещается в попап — уводим её в
  // горизонтальный скролл, а не сжимаем колонки
  @media (max-width: 900px) {
    &__table-wrap {
      overflow-x: auto;
      // overflow-y по умолчанию стал бы auto следом за overflow-x и дал
      // паразитный вертикальный скроллбар высотой в полосу прокрутки
      overflow-y: hidden;
    }

    // таблица целиком, вместе с пагинацией, держит свою ширину и уезжает
    // в скролл — иначе пагинация осталась бы узкой и разъезжалась со строками
    &__table {
      min-width: 700px;
    }
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: .1%;
    cursor: pointer;
    border-radius: 8px;

    &:nth-child(odd) {
      background: #F8F9FC;
    }

    &:hover {
      background: #EFF7FF;
    }

    &.selected {
      background: #ECF1FF;
    }
  }

  &__cell {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px;
    flex-grow: 1;
    flex-shrink: 0;
    box-sizing: border-box;
  }

  &__radio {
    flex-shrink: 0;
  }

  &__menu {
    margin-left: auto;
  }

  &__buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-top: 30px;
    flex-shrink: 0;

    & > :first-child {
      margin-right: auto;
    }

    // без кнопки «Добавить» слева прижимать нечего — оставшиеся кнопки
    // уходят вправо, как в любом другом попапе
    &.no-add {
      justify-content: flex-end;

      & > :first-child {
        margin-right: 0;
      }
    }
  }

  @media (max-width: 576px) {
    &__top {
      flex-direction: column;
      align-items: stretch;
      gap: 15px;
      margin-bottom: 20px;
    }

    &__title {
      font-size: 24px;
    }

    &__search {
      width: 100%;
    }

    &__buttons > * {
      flex: 1 1 auto;
    }
  }
}
</style>
